// Pure decisions of a bundle's date picking (a port of v1's
// BundleDatesSelection and its view): which dates can be chosen or let go,
// when saving is possible, and what the page says after.

export type ReschedulePolicy = {
  canReschedule?: boolean;
  rescheduleDaysBefore?: number;
  rescheduleHoursBefore?: number;
};

type BundleLike = {
  bundleTotalTickets?: number;
  bundleRecords?: { session: string }[];
};

type SessionLike = { _id: string; startDateTime: string; availability?: number };

/** The sessions the bundle already holds. */
export function recordSessionIds(bundle: BundleLike | undefined): string[] {
  return (bundle?.bundleRecords ?? []).map((r) => String(r.session));
}

/** Minutes past the hour of an instant in `timezone` (non-zero only in a
 * zone with a half- or quarter-hour offset). */
function minutesPastHourInZone(ms: number, timezone: string): number {
  const minute = new Intl.DateTimeFormat("en-GB", { timeZone: timezone, minute: "2-digit" }).formatToParts(new Date(ms)).find((p) => p.type === "minute")?.value;
  return Number(minute ?? 0);
}

/** Whether a booked session is too close to let go of: the school's
 * reschedule notice (days + hours before, default 1 day) has started, read
 * to the hour in the school's zone, as v1's isSessionWithinRescheduleCutoff
 * does. No rescheduling at all: always too close. */
export function isWithinRescheduleCutoff(
  session: { startDateTime: string },
  policy: ReschedulePolicy | undefined,
  timezone: string,
  now: number = Date.now(),
): boolean {
  if (!policy?.canReschedule) return true;
  const hours = (policy.rescheduleDaysBefore ?? 1) * 24 + (policy.rescheduleHoursBefore ?? 0);
  const cutoff = new Date(session.startDateTime).getTime() - hours * 3_600_000;
  // moment's isAfter(now, "hours") compares the start of the cutoff's hour.
  const d = new Date(cutoff);
  const startOfHour = cutoff - minutesPastHourInZone(cutoff, timezone) * 60_000 - d.getUTCSeconds() * 1000 - d.getUTCMilliseconds();
  return !(now < startOfHour);
}

/** Whether a date's row can't be clicked (v1's isDateDisabled): past dates;
 * sold-out dates the bundle doesn't hold; and booked dates that can't be let
 * go (no rescheduling, or inside the notice period). New dates can always be
 * added. */
export function isBundleDateDisabled(args: {
  session: SessionLike;
  bundle: BundleLike;
  originalIds: string[];
  policy: ReschedulePolicy | undefined;
  timezone: string;
  now?: number;
}): boolean {
  const now = args.now ?? Date.now();
  const { session } = args;
  if (new Date(session.startDateTime).getTime() < now) return true;
  if (Number(session.availability ?? 0) <= 0) return !args.originalIds.includes(session._id);
  const isBooked = recordSessionIds(args.bundle).includes(session._id);
  if (!args.policy?.canReschedule) return isBooked;
  if (!isBooked) return false;
  return isWithinRescheduleCutoff(session, args.policy, args.timezone, now);
}

/** A new selection with any booked date that may not be let go put back
 * (v1's onSelectionChange). */
export function protectSelection(args: {
  next: string[];
  originalIds: string[];
  sessions: SessionLike[];
  policy: ReschedulePolicy | undefined;
  timezone: string;
  now?: number;
}): string[] {
  const kept = args.originalIds.filter((id) => {
    if (args.next.includes(id)) return false;
    const session = args.sessions.find((s) => s._id === id);
    return !!session && isWithinRescheduleCutoff(session, args.policy, args.timezone, args.now);
  });
  return kept.length ? [...new Set([...args.next, ...kept])] : args.next;
}

/** Saving needs at least one date and a change from what's held. */
export function canSaveDates(selected: string[], bundle: BundleLike): boolean {
  if (selected.length === 0) return false;
  const held = recordSessionIds(bundle);
  const unchanged = selected.length === held.length && held.every((id) => selected.includes(id));
  return !unchanged;
}

export function classesLeft(bundle: BundleLike, selected: string[]): number {
  return Number(bundle.bundleTotalTickets ?? 0) - selected.length;
}

/** "You have 2 classes left to book" etc. */
export function classesLeftTitle(left: number): string {
  return `You have ${left} ${left === 1 ? "class" : "classes"} left to book`;
}

/** What the page says once the dates are saved. */
export function savedMessage(left: number, total: number, expires: string): string {
  if (left <= 0) return `Congratulations. All of your ${total} classes are booked.`;
  return `${left === 1 ? "1 class is" : `${left} classes are`} still left to book. You have until ${expires} to select your dates.`;
}

/** "Select date" / "Select dates". */
export function saveLabel(selectedCount: number): string {
  return selectedCount > 1 ? "Select dates" : "Select date";
}

/** Page size of the "see more dates" calls, as v1. */
export const DATES_PAGE_SIZE = 15;
